import type { ApiResponse } from "@/types";
import type {
  AppFeedbackRequest,
  AppFeedbackResponse,
  CreateFeedbackRequest,
  CreateFeedbackResponse,
  ListFeedbackResponse,
  ProfileResponse,
} from "../../../schemas";
import type {
  AddMcpServer,
  AgentTask,
  CreateChatRequest,
  CreateChatResponse,
  FetchEndpointRequestData,
  FetchEndpointResponse,
  GetChatResponse,
  GetEndpointsResponse,
  ListChatResponse,
  ListMessagesResponse,
  MutateSecret,
  RemoveMcpServer,
  SecretMutation,
  SerializableMcpConnection,
} from "../../../schemas/codegen";
import { chatWebSocketManager } from "../websocket/ChatWebSocketManager";
import type { CreateFeedbackParams } from "./feedback";
import { baseFetch } from "./fetch";

export const api = {
  getProfile: async (): Promise<ApiResponse<ProfileResponse>> => {
    return baseFetch<ApiResponse<ProfileResponse>>("/api/auth/profile");
  },

  listChats: async (): Promise<ApiResponse<ListChatResponse>> => {
    return baseFetch<ApiResponse<ListChatResponse>>("/api/chats");
  },

  getChat: async (chatId: string): Promise<ApiResponse<GetChatResponse>> => {
    return baseFetch<ApiResponse<GetChatResponse>>(`/api/chats/${chatId}`);
  },

  createChat: async (
    data: CreateChatRequest,
  ): Promise<ApiResponse<CreateChatResponse>> => {
    return baseFetch<ApiResponse<CreateChatResponse>>("/api/chats", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(data),
    });
  },

  listMessages: async (
    chatId: string,
  ): Promise<ApiResponse<ListMessagesResponse>> => {
    return baseFetch<ApiResponse<ListMessagesResponse>>(
      `/api/chats/${chatId}/messages`,
    );
  },

  getEndpoints: async (
    chatId: string,
  ): Promise<ApiResponse<GetEndpointsResponse>> => {
    return baseFetch<ApiResponse<GetEndpointsResponse>>(
      `/api/chats/${chatId}/endpoints`,
    );
  },

  fetchEndpointRequest: async (
    chatId: string,
    requestData: FetchEndpointRequestData,
  ): Promise<FetchEndpointResponse> => {
    const response = await baseFetch<ApiResponse<FetchEndpointResponse>>(
      `/api/chats/${chatId}/fetch-endpoint`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(requestData),
      },
    );
    return response.data;
  },

  /**
   * List all of a user's feedback on messages for a chat
   */
  listChatFeedback: async (
    chatId: string,
  ): Promise<ApiResponse<ListFeedbackResponse>> => {
    return baseFetch<ApiResponse<ListFeedbackResponse>>(
      `/api/feedback?chatId=${chatId}`,
      {
        method: "GET",
        credentials: "include",
      },
    );
  },

  /**
   * Give a thumbs up to a message
   */
  giveThumbsUp: async (
    params: CreateFeedbackParams,
  ): Promise<ApiResponse<CreateFeedbackResponse>> => {
    const feedback: CreateFeedbackRequest = {
      id: params.id,
      chatId: params.chatId,
      messageId: params.messageId,
      score: 1,
      text: params.additionalUserInput,
    };
    return baseFetch<ApiResponse<CreateFeedbackResponse>>("/api/feedback", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(feedback),
    });
  },

  /**
   * Give a thumbs down to a message
   */
  giveThumbsDown: async (
    params: CreateFeedbackParams,
  ): Promise<ApiResponse<CreateFeedbackResponse>> => {
    const feedback: CreateFeedbackRequest = {
      id: params.id,
      chatId: params.chatId,
      messageId: params.messageId,
      score: 0,
      text: params.additionalUserInput,
    };
    return baseFetch<ApiResponse<CreateFeedbackResponse>>("/api/feedback", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(feedback),
    });
  },

  /**
   * Undo a thumbs up or thumbs down
   */
  undoFeedback: async (
    feedbackId: string,
  ): Promise<ApiResponse<CreateFeedbackResponse>> => {
    return baseFetch<ApiResponse<CreateFeedbackResponse>>(
      `/api/feedback/${feedbackId}`,
      {
        method: "DELETE",
        credentials: "include",
      },
    );
  },

  /**
   * Log out the current user
   */
  logout: async (): Promise<void> => {
    return baseFetch<void>("/api/auth/logout", {
      method: "POST",
      credentials: "include",
    });
  },

  /**
   * Secrets/Environment Variables API
   */
  listSecrets: async (chatId: string) => {
    return baseFetch<ApiResponse<{ secretKeys: string[] }>>(
      `/api/chats/${chatId}/secrets`,
    );
  },

  /**
   * Mutate secrets via CHAT WebSocket
   */
  mutateSecrets: async (
    chatId: string,
    mutations: SecretMutation[],
  ): Promise<boolean> => {
    const connection = chatWebSocketManager.getConnection(chatId);

    const mutationCommand: MutateSecret = {
      type: "secret.mutate",
      mutations,
    };

    // Ensure connection is established
    if (!connection.isConnected()) {
      try {
        await connection.connect();
      } catch (error) {
        console.error(
          `Failed to connect to chat ${chatId} for secret mutation:`,
          error,
        );
        return false;
      }
    }

    // Send mutation command via CHAT WebSocket
    return connection.send(mutationCommand);
  },

  /**
   * Add/update a secret via CHAT WebSocket
   */
  setSecret: async (
    chatId: string,
    secretKey: string,
    secretValue: string,
  ): Promise<boolean> => {
    return api.mutateSecrets(chatId, [
      { type: "secret.set", secretKey, secretValue },
    ]);
  },

  /**
   * Delete a secret via CHAT WebSocket
   */
  deleteSecret: async (chatId: string, secretKey: string): Promise<boolean> => {
    return api.mutateSecrets(chatId, [{ type: "secret.delete", secretKey }]);
  },

  /**
   * List MCP server connections for a chat
   */
  listMcpServers: async (
    chatId: string,
  ): Promise<
    ApiResponse<{ connections: Record<string, SerializableMcpConnection> }>
  > => {
    return baseFetch<
      ApiResponse<{ connections: Record<string, SerializableMcpConnection> }>
    >(`/api/chats/${chatId}/mcp/servers`);
  },

  /**
   * List tasks for a chat session
   */
  listTasks: async (
    chatId: string,
  ): Promise<ApiResponse<{ tasks: AgentTask[] }>> => {
    return baseFetch<ApiResponse<{ tasks: AgentTask[] }>>(
      `/api/chats/${chatId}/tasks`,
    );
  },

  /**
   * Add MCP server via CHAT WebSocket
   */
  addMcpServer: async (
    chatId: string,
    url: string,
    name: string,
  ): Promise<boolean> => {
    const connection = chatWebSocketManager.getConnection(chatId);

    const addServerCommand: AddMcpServer = {
      type: "mcp.server.add",
      url,
      name,
    };

    // Ensure connection is established
    if (!connection.isConnected()) {
      try {
        await connection.connect();
      } catch (error) {
        console.error(
          `Failed to connect to chat ${chatId} for MCP server add:`,
          error,
        );
        return false;
      }
    }

    // Send add server command via CHAT WebSocket
    return connection.send(addServerCommand);
  },

  /**
   * Remove MCP server via CHAT WebSocket
   */
  removeMcpServer: async (
    chatId: string,
    serverId: string,
  ): Promise<boolean> => {
    const connection = chatWebSocketManager.getConnection(chatId);

    const removeServerCommand: RemoveMcpServer = {
      type: "mcp.server.remove",
      serverId,
    };

    // Ensure connection is established
    if (!connection.isConnected()) {
      try {
        await connection.connect();
      } catch (error) {
        console.error(
          `Failed to connect to chat ${chatId} for MCP server remove:`,
          error,
        );
        return false;
      }
    }

    // Send remove server command via CHAT WebSocket
    return connection.send(removeServerCommand);
  },

  /**
   * Submit app feedback
   */
  submitAppFeedback: async (
    data: AppFeedbackRequest,
  ): Promise<ApiResponse<AppFeedbackResponse>> => {
    return baseFetch<ApiResponse<AppFeedbackResponse>>("/api/app-feedback", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(data),
    });
  },
};
